import { useCallback, useState } from "react";
import type { OrderFormData, OrderResponse } from "@/schemas/order.schema";
import { getCheckoutAttribution, trackCheckoutError } from "@/utils/tracking";

/**
 * Shared order-submit core. POSTs the order to `/api/order` with attribution and
 * returns the payment URL (throws on any failure). Single source of truth for the
 * checkout request used by both the site-wide `CheckoutDrawer` (via `CheckoutContext`)
 * and the inline offer-modal form (`useOrderCheckout`).
 */
export async function createOrder(offerId: number, formData: OrderFormData): Promise<string> {
  const response = await fetch("/api/order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...formData, offerId, ...getCheckoutAttribution() }),
  });

  const data: OrderResponse = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || "Произошла ошибка при создании заказа");
  }
  if (!data.paymentUrl) {
    throw new Error("Не получена ссылка на оплату");
  }
  return data.paymentUrl;
}

export type OrderCheckoutState = "idle" | "submitting" | "success" | "error";

/**
 * Local checkout state machine for an inline (non-drawer) order form — e.g. a form
 * embedded directly in an offer modal. Deliberately NOT wired to `CheckoutContext`
 * so it does not trigger the global `CheckoutDrawer`. On success it redirects to the
 * payment URL after a short delay (mirrors the drawer behaviour).
 */
export function useOrderCheckout(productTitle: string) {
  const [state, setState] = useState<OrderCheckoutState>("idle");
  const [error, setError] = useState("");
  const [paymentUrl, setPaymentUrl] = useState("");

  const submit = useCallback(
    async (offerId: number, formData: OrderFormData) => {
      setState("submitting");
      setError("");
      try {
        const url = await createOrder(offerId, formData);
        setPaymentUrl(url);
        setState("success");
        setTimeout(() => {
          window.location.href = url;
        }, 2000);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Произошла ошибка при создании заказа";
        setError(msg);
        setState("error");
        trackCheckoutError(productTitle, msg);
      }
    },
    [productTitle],
  );

  return { state, error, paymentUrl, submit };
}
